/**
 * ExportImportPanel - Configuration save/load and device sync
 *
 * Operations:
 * - Save/Load JSON files locally
 * - Import config from connected device
 * - Upload config to connected device
 */

import { useState, useRef, useEffect } from 'react';
import { useConfigStore } from '@/store/configStore';
import {
  exportConfiguration,
  downloadConfiguration,
  parseConfiguration,
  configToStore,
} from '@/lib/configIO';
import {
  exportBinaryConfiguration,
} from '@/lib/binaryIO';
import { getSerialConnection, parseJsonFromCliResponse } from '@/lib/serialConnection';

export function ExportImportPanel() {
  const loadConfiguration = useConfigStore((s) => s.loadConfiguration);
  const jsonFileInputRef = useRef<HTMLInputElement>(null);

  const [statusMessage, setStatusMessage] = useState<string>('');
  const [statusType, setStatusType] = useState<'success' | 'error' | 'info'>('info');
  const [importErrors, setImportErrors] = useState<string[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [deviceImportLoading, setDeviceImportLoading] = useState(false);
  const [deviceUploadLoading, setDeviceUploadLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);

  // Track connection state
  useEffect(() => {
    const conn = getSerialConnection();
    setIsConnected(conn.isConnected());
    const unsubscribe = conn.onStateChange((state) => {
      setIsConnected(state === 'connected');
    });
    return () => unsubscribe();
  }, []);

  const showStatus = (message: string, type: 'success' | 'error' | 'info', duration = 3000) => {
    setStatusMessage(message);
    setStatusType(type);
    setImportErrors([]);
    if (duration > 0) {
      setTimeout(() => setStatusMessage(''), duration);
    }
  };

  // === Device operations ===

  const handleImportFromDevice = async () => {
    const conn = getSerialConnection();
    if (!conn.isConnected()) {
      showStatus('Not connected to device', 'error');
      return;
    }

    setDeviceImportLoading(true);
    showStatus('Reading configuration from device...', 'info', 0);

    try {
      const response = await conn.sendCommand('json config', 10000);

      if (response.raw.includes('ERR:')) {
        const errMatch = response.raw.match(/ERR:\s*(.+)/);
        showStatus(`Device error: ${errMatch?.[1] || 'Unknown error'}`, 'error');
        setDeviceImportLoading(false);
        return;
      }

      const data = parseJsonFromCliResponse(response.raw);
      if (!data || typeof data !== 'object') {
        showStatus('Failed to parse device response', 'error');
        setDeviceImportLoading(false);
        return;
      }

      const jsonStr = JSON.stringify(data);
      const result = parseConfiguration(jsonStr);

      if (!result.valid) {
        setStatusMessage('Configuration validation failed');
        setStatusType('error');
        setImportErrors(result.errors ?? ['Unknown validation error']);
        setDeviceImportLoading(false);
        return;
      }

      if (!result.config) {
        showStatus('Import failed: No configuration data', 'error');
        setDeviceImportLoading(false);
        return;
      }

      const storeConfig = configToStore(result.config);
      loadConfiguration(storeConfig);
      showStatus('Configuration imported from device', 'success');
    } catch (error) {
      showStatus(`Import failed: ${error instanceof Error ? error.message : 'Unknown error'}`, 'error', 5000);
    } finally {
      setDeviceImportLoading(false);
    }
  };

  const handleUploadToDevice = async () => {
    const conn = getSerialConnection();
    if (!conn.isConnected()) {
      showStatus('Not connected to device', 'error');
      return;
    }

    setDeviceUploadLoading(true);
    setUploadProgress(0);

    try {
      const arrayBuffer = exportBinaryConfiguration(useConfigStore.getState());
      const binaryData = new Uint8Array(arrayBuffer);

      showStatus(`Uploading ${binaryData.length} bytes...`, 'info', 0);

      const result = await conn.uploadConfig(binaryData, (percent) => {
        setUploadProgress(percent);
      });

      if (result.success) {
        showStatus('Configuration uploaded to device', 'success');
      } else {
        showStatus(`Upload failed: ${result.message || 'Unknown error'}`, 'error', 5000);
      }
    } catch (error) {
      showStatus(`Upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`, 'error', 5000);
    } finally {
      setDeviceUploadLoading(false);
      setUploadProgress(null);
    }
  };

  // === Local file operations ===

  const handleSaveJson = () => {
    try {
      const config = exportConfiguration(useConfigStore.getState());
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      const filename = `keyboard-config-${timestamp}.json`;
      downloadConfiguration(config, filename);
      showStatus(`Saved: ${filename}`, 'success');
    } catch (error) {
      showStatus(`Save failed: ${error instanceof Error ? error.message : 'Unknown error'}`, 'error', 5000);
    }
  };

  const handleLoadJson = () => {
    jsonFileInputRef.current?.click();
  };

  const handleJsonFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    showStatus('Reading file...', 'info', 0);

    try {
      const content = await file.text();
      const result = parseConfiguration(content);

      if (!result.valid) {
        setStatusMessage('Validation failed');
        setStatusType('error');
        setImportErrors(result.errors ?? ['Unknown validation error']);
        return;
      }

      if (!result.config) {
        showStatus('Import failed: No configuration data', 'error');
        return;
      }

      const storeConfig = configToStore(result.config);
      loadConfiguration(storeConfig);
      showStatus(`Loaded: ${file.name}`, 'success');
    } catch (error) {
      showStatus(`Import failed: ${error instanceof Error ? error.message : 'Unknown error'}`, 'error', 5000);
    } finally {
      if (jsonFileInputRef.current) {
        jsonFileInputRef.current.value = '';
      }
    }
  };

  const isLoading = deviceImportLoading || deviceUploadLoading;

  return (
    <>
      {/* Device Sync */}
      <div style={styles.section}>
        <div style={styles.sectionTitle}>Device</div>
        {isConnected ? (
          <div style={styles.buttonRow}>
            <button
              onClick={handleImportFromDevice}
              disabled={isLoading}
              style={{
                ...styles.button,
                ...styles.buttonDevice,
                opacity: isLoading ? 0.6 : 1,
              }}
            >
              {deviceImportLoading ? 'Reading...' : 'Read from Device'}
            </button>
            <button
              onClick={handleUploadToDevice}
              disabled={isLoading}
              style={{
                ...styles.button,
                ...styles.buttonUpload,
                opacity: isLoading ? 0.6 : 1,
              }}
            >
              {deviceUploadLoading ? `Sending ${uploadProgress ?? 0}%` : 'Send to Device'}
            </button>
          </div>
        ) : (
          <div style={styles.hint}>Connect to device first (use the connection bar above)</div>
        )}

        {/* Upload progress bar */}
        {uploadProgress !== null && (
          <div style={styles.progressTrack}>
            <div style={{ ...styles.progressFill, width: `${uploadProgress}%` }} />
          </div>
        )}
      </div>

      {/* Local Files */}
      <div style={styles.section}>
        <div style={styles.sectionTitle}>Local File</div>
        <div style={styles.buttonRow}>
          <button onClick={handleSaveJson} style={{ ...styles.button, ...styles.buttonSave }}>
            Save JSON
          </button>
          <button onClick={handleLoadJson} style={{ ...styles.button, ...styles.buttonLoad }}>
            Load JSON
          </button>
        </div>
        <input
          ref={jsonFileInputRef}
          type="file"
          accept=".json,application/json"
          onChange={handleJsonFileSelect}
          style={{ display: 'none' }}
        />
      </div>

      {/* Status message */}
      {statusMessage && (
        <div
          style={{
            ...styles.status,
            backgroundColor: statusType === 'error' ? '#f8d7da' : statusType === 'success' ? '#d4edda' : '#e2e3e5',
            color: statusType === 'error' ? '#721c24' : statusType === 'success' ? '#155724' : '#383d41',
          }}
        >
          {statusMessage}
        </div>
      )}

      {/* Validation errors */}
      {importErrors.length > 0 && (
        <div style={styles.errors}>
          <strong>Errors:</strong>
          <ul style={{ margin: '4px 0 0 0', paddingLeft: '20px' }}>
            {importErrors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
}

const styles = {
  section: {
    marginBottom: '12px',
  },
  sectionTitle: {
    fontSize: '12px',
    fontWeight: 600,
    color: '#495057',
    marginBottom: '6px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  } as React.CSSProperties,
  buttonRow: {
    display: 'flex',
    gap: '6px',
  },
  button: {
    flex: 1,
    padding: '8px 12px',
    fontSize: '13px',
    fontWeight: 500,
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
  } as React.CSSProperties,
  buttonDevice: {
    backgroundColor: '#17a2b8',
  },
  buttonUpload: {
    backgroundColor: '#e94560',
  },
  buttonSave: {
    backgroundColor: '#28a745',
  },
  buttonLoad: {
    backgroundColor: '#007bff',
  },
  hint: {
    fontSize: '12px',
    color: '#6c757d',
    fontStyle: 'italic',
  } as React.CSSProperties,
  progressTrack: {
    marginTop: '6px',
    height: '4px',
    backgroundColor: '#e9ecef',
    borderRadius: '2px',
    overflow: 'hidden',
  } as React.CSSProperties,
  progressFill: {
    height: '100%',
    backgroundColor: '#e94560',
    transition: 'width 0.1s',
  } as React.CSSProperties,
  status: {
    padding: '8px',
    fontSize: '12px',
    borderRadius: '4px',
    marginBottom: '8px',
  },
  errors: {
    padding: '8px',
    fontSize: '11px',
    borderRadius: '4px',
    backgroundColor: '#f8d7da',
    color: '#721c24',
    maxHeight: '150px',
    overflowY: 'auto',
  } as React.CSSProperties,
};
